import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || '';
const cleanBackendUrl = rawBackendUrl.trim().replace(/\/+$/, '');
const API_URL = cleanBackendUrl
  ? (cleanBackendUrl.endsWith('/api') ? cleanBackendUrl : `${cleanBackendUrl}/api`)
  : '/api';

// Global debounce trackers to prevent alert storming on concurrent requests
let lastNetErrorToastTime = 0;
let lastOfflineToastTime = 0;
let lastTimeoutToastTime = 0;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (e && typeof e === 'object') {
          // Pydantic v2 errors: {type, loc, msg, input, url}.
          // Render as "field.path: human message" so the toast is actionable.
          const loc = Array.isArray(e.loc)
            ? e.loc.filter((x) => x !== 'body').join('.')
            : '';
          const msg = typeof e.msg === 'string' ? e.msg : JSON.stringify(e);
          return loc ? `${loc}: ${msg}` : msg;
        }
        return String(e);
      })
      .filter(Boolean)
      .join('; ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return JSON.stringify(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  // Stage 73.20dg — Reporting-manager status + view switcher.
  //   isReportingManager: true if the logged-in user is mapped as RM for ≥1 active employee
  //   activeView: 'employee' | 'manager' — controls which sidebar sections show
  const [isReportingManager, setIsReportingManager] = useState(false);
  const [managerTeamSize, setManagerTeamSize] = useState(0);
  const [activeView, setActiveViewInternal] = useState(() => {
    try { return localStorage.getItem('activeView') || 'employee'; }
    catch { return 'employee'; }
  });
  const setActiveView = (v) => {
    setActiveViewInternal(v);
    try { localStorage.setItem('activeView', v); } catch { /* ignore */ }
  };

  // Setup axios interceptor for 401 auto-refresh with de-duplication
  useEffect(() => {
    let refreshPromise = null;

    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalConfig = error.config;
        const url = originalConfig?.url || '';

        // Don't intercept the refresh endpoint itself (prevents infinite loops)
        if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
          if (url.includes('/auth/refresh')) {
            setUser(false);
            try {
              localStorage.removeItem('hadSession');
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
            } catch (_) {}
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
          return Promise.reject(error);
        }

        // Stage 73.20ee — Skip the refresh dance for callers that have opted
        // out via `config._skipAuthRetry = true`. The initial `checkAuth()`
        // and `_fetchManagerStatus()` probes on page load use this flag so
        // that unauthenticated visitors on /login don't see three noisy 401
        // errors in the console (probe → refresh → retry).
        if (error.response?.status === 401 && originalConfig?._skipAuthRetry) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalConfig._retry) {
          originalConfig._retry = true;
          try {
            // De-duplicate concurrent refresh calls
            if (!refreshPromise) {
              const localRefreshToken = localStorage.getItem('refresh_token');
              refreshPromise = axios
                .post(
                  `${API_URL}/auth/refresh`,
                  { refresh_token: localRefreshToken },
                  { withCredentials: true }
                )
                .then((res) => {
                  if (res.data?.access_token) {
                    localStorage.setItem('access_token', res.data.access_token);
                  }
                  if (res.data?.refresh_token) {
                    localStorage.setItem('refresh_token', res.data.refresh_token);
                  }
                  return res;
                })
                .finally(() => { refreshPromise = null; });
            }
            await refreshPromise;
            const newAccessToken = localStorage.getItem('access_token');
            if (newAccessToken) {
              if (!originalConfig.headers) originalConfig.headers = {};
              originalConfig.headers['Authorization'] = `Bearer ${newAccessToken}`;
            }
            // Retry original request with credentials and updated Authorization header
            return axios({ ...originalConfig, withCredentials: true });
          } catch (refreshError) {
            setUser(false);
            try {
              localStorage.removeItem('hadSession');
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
            } catch (_) {}
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }
        // === RESILIENT NETWORK & ERROR HANDLING =================================
        // 1. Never show network errors for canceled or aborted requests (navigation, unmount, search debounce)
        const isCanceled =
          axios.isCancel(error) ||
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED' ||
          originalConfig?.signal?.aborted;

        if (isCanceled) {
          return Promise.reject(error);
        }

        // 2. Client device is completely offline
        const isClientOffline =
          typeof window !== 'undefined' &&
          typeof navigator !== 'undefined' &&
          navigator.onLine === false;

        if (isClientOffline) {
          const now = Date.now();
          if (now - lastOfflineToastTime > 15000) {
            lastOfflineToastTime = now;
            try {
              const { toast } = await import('sonner');
              toast.warning('You appear to be offline. Please check your internet connection.', {
                id: 'client-offline',
                duration: 5000,
              });
            } catch (_) { /* noop */ }
          }
          return Promise.reject(error);
        }

        // 3. Request timeout
        if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
          const now = Date.now();
          if (now - lastTimeoutToastTime > 10000) {
            lastTimeoutToastTime = now;
            try {
              const { toast } = await import('sonner');
              toast.error('The request timed out. Please try again.', {
                id: 'request-timeout',
                duration: 5000,
              });
            } catch (_) { /* noop */ }
          }
          return Promise.reject(error);
        }

        // 4. Genuine network connectivity failure
        // Axios sets error.response === undefined only when no response was received (e.g. server down, DNS error).
        // Rate-limit globally across all concurrent failing requests (max 1 toast every 15s) to prevent toast storming.
        if (!error.response && !originalConfig?._skipNetToast) {
          const now = Date.now();
          if (now - lastNetErrorToastTime > 15000) {
            lastNetErrorToastTime = now;
            try {
              const { toast } = await import('sonner');
              const isDev = process.env.NODE_ENV === 'development';
              const message = isDev
                ? `Backend unreachable at ${API_URL}. Check REACT_APP_BACKEND_URL in frontend/.env and confirm the backend is running.`
                : 'Unable to connect to the backend server. Please verify your connection or try again later.';
              toast.error(message, {
                id: 'backend-unreachable',
                duration: 6000,
              });
            } catch (_) { /* noop */ }
          }
        }
        // === SAFE-RENDER NORMALIZATION ===========================================
        // FastAPI / Pydantic v2 returns 422 errors as
        //   {detail: [{type, loc, msg, input, url}, …]}
        // and 4xx business errors sometimes as
        //   {detail: {msg: "...", code: "..."}}
        // Both shapes are objects → if ANY caller does
        //   toast.error(err.response.data.detail)
        // React tries to render the object and crashes with
        //   "Objects are not valid as a React child".
        // We can't realistically audit 130+ catch blocks, so we normalise
        // every error response's `detail` field to a plain string right here
        // before it reaches user code. Callers that already use `formatApiError`
        // continue to work — the helper handles both string and array shapes.
        if (error.response?.data?.detail !== undefined) {
          error.response.data.detail = formatApiErrorDetail(error.response.data.detail);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
      axios.interceptors.request.eject(reqInterceptor);
    };
  }, []);

  const _fetchManagerStatus = useCallback(async () => {
    try {
      // Stage 73.20ee — Silent probe. If unauthenticated, don't fire refresh.
      const { data } = await axios.get(`${API_URL}/me/manager-status`, {
        withCredentials: true, _skipAuthRetry: true,
      });
      setIsReportingManager(!!data?.is_reporting_manager);
      setManagerTeamSize(data?.team_size || 0);
      // If user is not a manager, force back to employee view.
      if (!data?.is_reporting_manager) setActiveView('employee');
    } catch {
      setIsReportingManager(false);
      setManagerTeamSize(0);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    // Stage 73.20ee — Skip the network probe entirely when this browser has
    // never had a session (no `hadSession` flag). Fresh visitors + logged-out
    // users hitting /login should see ZERO 401s in the console.
    let hadSession = false;
    try { hadSession = localStorage.getItem('hadSession') === '1'; } catch { /* ignore */ }
    if (!hadSession) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
      const actTenant = data.active_tenant_id || data.tenant_id || null;
      const actCompany = data.active_company_id || data.company_id || null;
      setActiveTenantId(actTenant);
      setActiveCompanyId(actCompany);
      setAvailableCompanies(data.available_companies || []);

      if (data.company || data.company_name) {
        try {
          const compContext = data.company || {
            id: actCompany || actTenant || '',
            company_id: actCompany || actTenant || '',
            name: data.company_name || data.tenant_name || '',
            code: data.company_code || '',
            database_name: data.database_name || ''
          };
          localStorage.setItem('selectedCompany', JSON.stringify(compContext));
        } catch { /* ignore */ }
      }

      await _fetchManagerStatus();
    } catch (error) {
      // Cookie expired or invalid — clear the flag so the next visit is silent.
      try {
        localStorage.removeItem('hadSession');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      } catch { /* ignore */ }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [_fetchManagerStatus]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Heartbeat to keep session fresh (every 10 min) while user is active
  useEffect(() => {
    if (!user || user === false) return;
    const heartbeat = setInterval(() => {
      axios.get(`${API_URL}/auth/me`, { withCredentials: true }).catch(() => {});
    }, 10 * 60 * 1000); // 10 minutes
    return () => clearInterval(heartbeat);
  }, [user]);

  const login = async (email, password, tenantId = '') => {
    try {
      const payload = { email, password };
      if (tenantId) {
        payload.tenant_id = tenantId;
        payload.organization_id = tenantId;
      }
      console.log('[AuthContext] login payload:', payload);
      const { data } = await axios.post(
        `${API_URL}/auth/login`,
        payload,
        { withCredentials: true }
      );
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      // Clear stale previous session company caches
      try {
        localStorage.removeItem('selectedCompany');
        sessionStorage.removeItem('selectedCompany');
        localStorage.removeItem('selectedTenant');
        sessionStorage.removeItem('selectedTenant');
      } catch { /* ignore */ }

      // After login, fetch full user with tenant context and manager status in parallel
      const [meRes] = await Promise.all([
        axios.get(`${API_URL}/auth/me`, { withCredentials: true }),
        _fetchManagerStatus()
      ]);
      const userData = meRes?.data || {};
      setUser(userData);
      const actTenant = userData.active_tenant_id || userData.tenant_id || null;
      const actCompany = userData.active_company_id || userData.company_id || null;
      setActiveTenantId(actTenant);
      setActiveCompanyId(actCompany);
      setAvailableCompanies(userData.available_companies || []);

      // Store authenticated company context as single source of truth
      if (userData.company || userData.company_name) {
        try {
          const compContext = userData.company || {
            id: actCompany || actTenant || '',
            company_id: actCompany || actTenant || '',
            name: userData.company_name || userData.tenant_name || '',
            code: userData.company_code || '',
            database_name: userData.database_name || ''
          };
          localStorage.setItem('selectedCompany', JSON.stringify(compContext));
        } catch { /* ignore */ }
      }

      try { localStorage.setItem('hadSession', '1'); } catch { /* ignore */ }
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    try {
      localStorage.removeItem('hadSession');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('selectedCompany');
      sessionStorage.removeItem('selectedCompany');
      localStorage.removeItem('selectedTenant');
      sessionStorage.removeItem('selectedTenant');
    } catch { /* ignore */ }
    setUser(false);
    setActiveTenantId(null);
    setActiveCompanyId(null);
    setIsReportingManager(false);
    setManagerTeamSize(0);
  };

  const switchTenant = async (tenantId, companyId) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/auth/switch-tenant`,
        { tenant_id: tenantId, company_id: companyId || '' },
        { withCredentials: true }
      );
      setActiveTenantId(data.tenant_id || tenantId);
      setActiveCompanyId(data.company_id || companyId || '');
      await checkAuth();
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail),
      };
    }
  };

  const switchCompany = async (companyId) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/auth/switch-tenant`,
        { tenant_id: activeTenantId || '', company_id: companyId },
        { withCredentials: true }
      );
      setActiveCompanyId(data.company_id || companyId);
      await checkAuth();
      return { success: true, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail),
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    refresh: checkAuth,
    activeTenantId,
    activeCompanyId,
    availableCompanies,
    switchTenant,
    switchCompany,
    // Stage 73.20dg — RM view switcher
    isReportingManager,
    managerTeamSize,
    activeView,
    setActiveView,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
